import React, { useState, useMemo } from "react"
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js"

import { stripePromise } from "../../../utils/stripe"
import { funEU } from "../../../utils/firebase"

import Button from "../../ui/button/button"

import styles from "./customer-pay.module.scss"

const createCustomerPaymentIntentCallable = funEU.httpsCallable("createCustomerPaymentIntent")

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Customer-facing payment component for the mybooking portal.
 *
 * Flow:
 *   1. Customer enters amount + name/email (handled by parent component)
 *   2. Customer picks a payment method here: Card or Bank Transfer
 *   3. Stripe PaymentElement embeds on-page for the chosen method
 *   4. Customer fills in details, submits
 *   5a. Card → 3DS iframe in-page if needed → success state on mybooking
 *   5b. Bank Transfer → redirects to bank's auth → returns to `/payment-return`
 *
 * No auth required (customer not logged in). The createCustomerPaymentIntent
 * cloud function bounds the amount to the booking's outstanding balance so
 * creating PIs is safe to expose publicly.
 */
export default function CustomerPay({ bookingID, amount, payerFirstName, payerLastName, payerEmail, returnUrl, onSuccess }) {
	if (!stripePromise) {
		return (
			<div className={styles.error}>
				Card payment is temporarily unavailable. Please contact your sales agent to pay another way.
			</div>
		)
	}

	return <MethodPicker {...{ bookingID, amount, payerFirstName, payerLastName, payerEmail, returnUrl, onSuccess }} />
}

function MethodPicker(props) {
	const [method, setMethod] = useState(null)

	if (!method) {
		return (
			<div className={styles.methodPicker}>
				<h3 className={styles.methodHeading}>How would you like to pay?</h3>

				<div className={styles.methodOptions}>
					<button
						type="button"
						className={styles.methodOption}
						onClick={() => setMethod("card")}>
						<div className={styles.methodTitle}>Pay by card</div>
						<div className={styles.methodSub}>Debit or credit card - including Apple &amp; Google Pay.</div>
					</button>

					<button
						type="button"
						className={styles.methodOption}
						onClick={() => setMethod("pay_by_bank")}>
						<div className={styles.methodTitle}>Pay by bank transfer</div>
						<div className={styles.methodSub}>Pay directly from your bank app.</div>
					</button>
				</div>
			</div>
		)
	}

	return (
		<>
			<button
				type="button"
				className={styles.changeMethod}
				onClick={() => setMethod(null)}>
				← Choose a different payment method
			</button>

			<PayForm
				{...props}
				method={method}
			/>
		</>
	)
}

function PayForm(props) {
	const { amount, method } = props

	const elementsOptions = useMemo(
		() => ({
			mode: "payment",
			amount: Math.round(Number(amount || 0) * 100),
			currency: "gbp",
			paymentMethodTypes: [method],
			appearance: {
				theme: "stripe",
				variables: {
					colorPrimary: "#e41f13",
					fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
					borderRadius: "6px",
				},
			},
		}),
		[method, amount],
	)

	return (
		<Elements
			key={`${method}-${amount}`}
			stripe={stripePromise}
			options={elementsOptions}>
			<PaymentForm {...props} />
		</Elements>
	)
}

function PaymentForm({ bookingID, amount, payerFirstName, payerLastName, payerEmail, returnUrl, method, onSuccess }) {
	const stripe = useStripe()
	const elements = useElements()

	const [submitting, setSubmitting] = useState(false)
	const [errorMessage, setErrorMessage] = useState("")
	const [successMessage, setSuccessMessage] = useState("")

	const submit = async () => {
		if (!stripe || !elements) return

		setSubmitting(true)
		setErrorMessage("")

		// Validate the PaymentElement fields client-side first.
		const submitResult = await elements.submit()
		if (submitResult.error) {
			setErrorMessage(submitResult.error.message || "Please check the form and try again.")
			setSubmitting(false)
			return
		}

		// Mint the PaymentIntent on the server. Returns a clientSecret which
		// confirmPayment uses to actually take the money. Server validates
		// the amount against the booking's outstanding balance.
		let clientSecret
		try {
			const response = await createCustomerPaymentIntentCallable({
				bookingID,
				amount,
				method,
				payerFirstName,
				payerLastName,
				payerEmail,
				returnUrl,
			})

			clientSecret = response?.data?.clientSecret
			if (!clientSecret) throw new Error("No client secret returned.")
		} catch (err) {
			setErrorMessage(err?.message || "Could not start the payment. Please try again.")
			setSubmitting(false)
			return
		}

		// Confirm the payment. `redirect: 'if_required'` means card payments
		// stay in-page (3DS iframe pops if needed), while bank transfers
		// redirect to the bank's auth page (and come back to returnUrl).
		const confirmResult = await stripe.confirmPayment({
			elements,
			clientSecret,
			confirmParams: {
				return_url: returnUrl,
				receipt_email: payerEmail,
				payment_method_data: {
					billing_details: {
						name: `${payerFirstName} ${payerLastName}`.trim(),
						email: payerEmail,
					},
				},
			},
			redirect: "if_required",
		})

		if (confirmResult.error) {
			setErrorMessage(confirmResult.error.message || "Payment could not be completed.")
			setSubmitting(false)
			return
		}

		// Card success path (no redirect happened).
		if (confirmResult.paymentIntent?.status === "succeeded") {
			setSuccessMessage("Payment received. Thank you — this will appear on your booking shortly.")
			setSubmitting(false)
			if (typeof onSuccess === "function") onSuccess(confirmResult.paymentIntent)
			return
		}

		// Card needs additional action (3DS challenge that wasn't auto-handled,
		// rare with redirect:'if_required' but possible). Show a gentle message.
		setErrorMessage("Additional authentication is required. Please try again or contact us if the problem persists.")
		setSubmitting(false)
	}

	if (successMessage) {
		return (
			<div className={styles.success}>
				<div className={styles.successIcon}>✓</div>
				<p>{successMessage}</p>
			</div>
		)
	}

	return (
		<div className={styles.formWrap}>
			<PaymentElement
				options={{
					layout: "tabs",
					defaultValues: {
						billingDetails: {
							name: `${payerFirstName} ${payerLastName}`.trim(),
							email: payerEmail,
						},
					},
				}}
			/>

			{errorMessage && <p className={styles.error}>{errorMessage}</p>}

			<Button
				label={`Pay £${formatGbp(amount)}`}
				fullWidth={true}
				loading={submitting}
				loadingText="Processing..."
				onClick={submit}
				disabled={!stripe}
			/>

			<p className={styles.disclaimer}>
				Payments are processed securely by Stripe. Golf Holidays Direct never sees or stores your card or bank details.
			</p>
		</div>
	)
}
